// Visualizer colors. Resolution order: defaults < CSS custom properties on the
// canvas (or an ancestor) < the `theme` option.

export interface VizTheme {
  background: string;
  /** Fingerboard wood. */
  wood: string;
  fret: string;
  string: string;
  inlay: string;
  /** Label text (fret numbers, tuning, instrument name). */
  text: string;
  /** Active (sounding/selected) notes. */
  active: string;
  /** Context notes (rest of the bar). */
  context: string;
  /** Hover highlight. */
  accent: string;
  /** Text drawn on active dots. */
  activeText: string;
  /** Text drawn on context dots. */
  contextText: string;
  nut: string;
  capo: string;
  whiteKey: string;
  blackKey: string;
}

export const DEFAULT_VIZ_THEME: Readonly<VizTheme> = {
  background: '#1f2024',
  wood: '#2a2b30',
  fret: '#8b8d96',
  string: '#c9cbd3',
  inlay: '#50525b',
  text: '#e7e7ea',
  active: '#f0a43a',
  context: '#3a3b40',
  accent: '#ef6a4c',
  activeText: '#1f2024',
  contextText: '#e7e7ea',
  nut: '#d9d5c7',
  capo: '#5b5d66',
  whiteKey: '#e6e7eb',
  blackKey: '#18191c',
};

/** CSS custom property for each theme key (`--viz-active`, `--viz-white-key`, …). */
export const VIZ_CSS_VARS: Readonly<Record<keyof VizTheme, string>> = {
  background: '--viz-bg',
  wood: '--viz-wood',
  fret: '--viz-fret',
  string: '--viz-string',
  inlay: '--viz-inlay',
  text: '--viz-text',
  active: '--viz-active',
  context: '--viz-context',
  accent: '--viz-accent',
  activeText: '--viz-active-text',
  contextText: '--viz-context-text',
  nut: '--viz-nut',
  capo: '--viz-capo',
  whiteKey: '--viz-white-key',
  blackKey: '--viz-black-key',
};

export const VIZ_FONT = '600 11px Inter, system-ui, sans-serif';

/** Theme values set as CSS custom properties on (or inherited by) an element. */
export function themeFromCss(el: Element): Partial<VizTheme> {
  const out: Partial<VizTheme> = {};
  if (typeof getComputedStyle !== 'function') return out;
  const cs = getComputedStyle(el);
  for (const key of Object.keys(VIZ_CSS_VARS) as (keyof VizTheme)[]) {
    const v = cs.getPropertyValue(VIZ_CSS_VARS[key]).trim();
    if (v) out[key] = v;
  }
  return out;
}

export function resolveTheme(el: Element | null, theme?: Partial<VizTheme>): VizTheme {
  return { ...DEFAULT_VIZ_THEME, ...(el ? themeFromCss(el) : {}), ...(theme ?? {}) };
}

export function sameTheme(a?: Partial<VizTheme>, b?: Partial<VizTheme>): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a) as (keyof VizTheme)[];
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => a[k] === b[k]);
}
